import { Icon, keyframes, styled } from '@moleculeone/ui';
import * as Dialog from '@radix-ui/react-dialog';
import { Target } from '@retro-ui/api-contract';

import { CompoundEditor, CompoundEditorProps } from '@f/components/shared/compound-editor/CompoundEditor';
import { ActionButtons } from '@f/components/shared/compound-editor/components/ActionButtons/ActionButtons';

import { PROMPT_BEFORE_LOSING_ANALOGS, useAnalogs } from '../hooks/useAnalogs';

import { AnalogsActions } from './AnalogsActions';
import { AnalogsPrompt } from './AnalogsPrompt';
import { AnalogsSelection } from './AnalogsSelection';

const overlayShow = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 },
});

const contentShow = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 },
});

const DialogOverlay = styled(Dialog.Overlay, {
  backgroundColor: '$blackA6',
  position: 'fixed',
  zIndex: 4,
  inset: 0,
  animation: `${overlayShow} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
});

const DialogContent = styled(Dialog.Content, {
  'position': 'fixed',
  'zIndex': 10,
  'top': '12vh',
  'left': 'calc((100vw - 70rem) / 2)',
  'width': '70rem',
  '&:focus': { outline: 'none' },
});

const DialogContentInner = styled('div', {
  position: 'relative',
  height: '100%',
  width: '100%',
  padding: 30,
  backgroundColor: '$slate2',
  borderRadius: 6,
  boxShadow: 'hsl(206 22% 7% / 35%) 0px 10px 38px -10px, hsl(206 22% 7% / 20%) 0px 10px 20px -15px',
  animation: `${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1)`,
});

const DialogClose = styled(Dialog.Close, {
  'all': 'unset',
  'cursor': 'pointer',
  'fontFamily': 'inherit',
  'borderRadius': '100%',
  'height': 25,
  'width': 25,
  'display': 'inline-flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'color': '$slate11',
  'position': 'absolute',
  'top': 2,
  'right': 2,
  '&:hover': { backgroundColor: '$slate4', color: '$purple9' },
  '&:focus': { boxShadow: `0 0 0 2px $colors$slate7`, color: '$purple9' },
});

const ContentContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$4',
  alignItems: 'center',
});

const CompoundEditorContainer = styled('div', {
  variants: {
    isAnalogGenerationOpen: {
      true: {
        display: 'flex',
      },
    },
  },
});

interface CompoundEditorDialogProps
  extends Omit<CompoundEditorProps, 'addLocationPrompt' | 'pruneLocationPrompts' | 'locationAnalogPromptNodes'> {
  isOpen: boolean;
  onTargetIndexChange: (indexDiff: number) => void;
  currentTargetIndex: number;
  targetCount: number;
  onNewEmptyTargetAdd: () => void;
  onTargetDuplicate: () => void;
  isAnalogGenerationOpen: boolean;
  setAnalogGenerationOpen: (isGenerating: boolean) => void;
  onAddAnalogsToSearch: (index: number, analogs: Target[]) => void;
  isSmilesError: boolean;
}

export function CompoundEditorDialog(props: CompoundEditorDialogProps) {
  const {
    isOpen,
    setOpen,
    smiles,
    name,
    onSmilesChange,
    onNameChange,
    onTargetIndexChange,
    currentTargetIndex,
    targetCount,
    onNewEmptyTargetAdd,
    onTargetDuplicate,
    isAnalogGenerationOpen,
    setAnalogGenerationOpen,
    onAddAnalogsToSearch,
    isSmilesError,
  } = props;

  const analogsProps = useAnalogs({ smiles });

  const toggleDialogOpen = (open: boolean) => {
    if (!open) {
      const isConfirmed =
        analogsProps.analogs && analogsProps.analogs.length !== 0 ? window.confirm(PROMPT_BEFORE_LOSING_ANALOGS) : true;
      if (isConfirmed) {
        setOpen(false);
        analogsProps.resetAnalogState();
      }
    } else {
      setOpen(true);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={toggleDialogOpen}>
      <Dialog.Portal>
        <DialogOverlay>
          <DialogContent
            data-cy="compound-editor-dialog"
            onEscapeKeyDown={event => {
              event.preventDefault();
            }}
            onOpenAutoFocus={event => {
              event.preventDefault();
            }}
          >
            <ContentContainer>
              <DialogContentInner css={{ padding: isAnalogGenerationOpen && analogsProps.analogs ? 0 : 30 }}>
                {isAnalogGenerationOpen && analogsProps.analogs ? (
                  <AnalogsSelection
                    {...analogsProps}
                    smiles={smiles}
                    analogs={analogsProps.analogs}
                    onBackToEditor={() => {
                      setAnalogGenerationOpen(false);
                      analogsProps.resetAnalogState();
                    }}
                    onAddSelectedAnalogs={() => {
                      onAddAnalogsToSearch(
                        currentTargetIndex,
                        analogsProps.selectedAnalogs.map(c => ({ smiles: c.smiles, name: c.name ?? '' }))
                      );
                      setOpen(false);
                    }}
                  />
                ) : (
                  <>
                    <CompoundEditorContainer isAnalogGenerationOpen={isAnalogGenerationOpen}>
                      <CompoundEditor
                        smiles={smiles}
                        name={name}
                        onSmilesChange={onSmilesChange}
                        onNameChange={onNameChange}
                        setOpen={setOpen}
                        currentTargetIndex={currentTargetIndex}
                        isAnalogGenerationOpen={isAnalogGenerationOpen}
                        // addLocationPrompt={addLocationPrompt}
                        // pruneLocationPrompts={pruneLocationPrompts}
                        // locationAnalogPromptNodes={locationAnalogPromptNodes}
                        isSmilesError={isSmilesError}
                      />
                      {isAnalogGenerationOpen && (
                        <AnalogsPrompt
                          analogsPrompt={analogsProps.analogsPrompt}
                          setAnalogsPrompt={analogsProps.setAnalogsPrompt}
                        />
                      )}
                    </CompoundEditorContainer>
                    {isAnalogGenerationOpen && (
                      <AnalogsActions
                        onBack={() => {
                          setAnalogGenerationOpen(false);
                        }}
                        generateAnalogs={analogsProps.generateAnalogs}
                        isGeneratingAnalogs={analogsProps.isGeneratingAnalogs}
                      />
                    )}
                  </>
                )}

                <DialogClose aria-label="Close">
                  <Icon icon="close" />
                </DialogClose>
              </DialogContentInner>

              <ActionButtons
                onTargetIndexChange={onTargetIndexChange}
                currentTargetIndex={currentTargetIndex}
                targetCount={targetCount}
                onNewEmptyTargetAdd={onNewEmptyTargetAdd}
                onTargetDuplicate={onTargetDuplicate}
                isAnalogGenerationOpen={isAnalogGenerationOpen}
                setAnalogGenerationOpen={open => {
                  if (!open) {
                    const isConfirmed =
                      analogsProps.analogs && analogsProps.analogs.length !== 0
                        ? window.confirm(PROMPT_BEFORE_LOSING_ANALOGS)
                        : true;
                    if (isConfirmed) {
                      setAnalogGenerationOpen(false);
                      analogsProps.resetAnalogState();
                    }
                  } else {
                    setAnalogGenerationOpen(true);
                  }
                }}
                smiles={smiles}
                name={name}
                css={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '$5' }}
              />
            </ContentContainer>
            <DialogClose aria-label="Close">
              <Icon icon="close" />
            </DialogClose>
          </DialogContent>
        </DialogOverlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
